import { useRef, useState } from 'react';
import type { NewsPost, PostAudience } from '@gl/shared';

import { useSaveNewsPost, useUploadImage } from '../api/hooks.js';
import { Markdown } from './Markdown.js';
import { Modal } from './Modal.js';

interface Props {
  post: NewsPost | null;
  onClose: () => void;
}

export function PostEditor({ post, onClose }: Props) {
  const [title, setTitle] = useState(post?.title ?? '');
  const [body, setBody] = useState(post?.body ?? '');
  const [audience, setAudience] = useState<PostAudience>(post?.audience ?? 'users');
  const [published, setPublished] = useState(post?.publishedAt !== null && post !== null);
  const [preview, setPreview] = useState(false);

  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const save = useSaveNewsPost();
  const upload = useUploadImage();

  /** Inserts the uploaded image's markdown at the cursor. */
  const insertImage = (file: File) => {
    upload.mutate(file, {
      onSuccess: (result) => {
        const textarea = bodyRef.current;
        const at = textarea?.selectionStart ?? body.length;
        setBody(`${body.slice(0, at)}\n\n${result.markdown}\n\n${body.slice(at)}`);
      },
    });
  };

  return (
    <Modal
      open
      wide
      title={post ? 'Edit post' : 'New post'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!title.trim() || !body.trim() || save.isPending}
            onClick={() =>
              save.mutate(
                { id: post?.id, input: { title: title.trim(), body, audience, published } },
                { onSuccess: onClose },
              )
            }
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="label" htmlFor="post-title">
            Title
          </label>
          <input
            id="post-title"
            className="input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="post-audience">
              Audience
            </label>
            <select
              id="post-audience"
              className="input"
              value={audience}
              onChange={(event) => setAudience(event.target.value as PostAudience)}
            >
              <option value="users">Members only</option>
              <option value="public">Public</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 pb-2 text-sm text-field-200">
              <input
                type="checkbox"
                className="h-4 w-4 accent-brass-500"
                checked={published}
                onChange={(event) => setPublished(event.target.checked)}
              />
              Published
            </label>
          </div>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="label mb-0">Body (markdown)</span>
            <div className="flex items-center gap-2">
              <label className="btn-ghost cursor-pointer px-2 py-1 text-xs">
                {upload.isPending ? 'Uploading…' : 'Insert image'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) insertImage(file);
                    event.target.value = '';
                  }}
                />
              </label>
              <button
                type="button"
                className="btn-ghost px-2 py-1 text-xs"
                onClick={() => setPreview((value) => !value)}
              >
                {preview ? 'Edit' : 'Preview'}
              </button>
            </div>
          </div>

          {preview ? (
            <div className="min-h-[16rem] rounded-md border border-field-700 bg-field-950 px-4 py-3">
              <Markdown>{body || '_Nothing to preview yet._'}</Markdown>
            </div>
          ) : (
            <textarea
              ref={bodyRef}
              className="input min-h-[16rem] font-mono text-xs leading-relaxed"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="Write the post in markdown…"
            />
          )}
        </div>

        {upload.error ? (
          <p className="text-sm text-red-300">{(upload.error as Error).message}</p>
        ) : null}
        {save.error ? <p className="text-sm text-red-300">{(save.error as Error).message}</p> : null}
      </div>
    </Modal>
  );
}
